/* @refresh reload */
import { render } from "solid-js/web";

import { setServiceWorkerUpdate } from "@fieldcheck/shared";

import { App } from "@/app";
import { isTauriRuntime } from "@/lib/platform";
import { hydrateSecureTokens, primeActiveNativeToken } from "@/lib/secure-token";
import "@/styles/global.css";

// PWA: auto-update de UNA sola recarga. El `sw.js` (workbox) ya hace `skipWaiting()` al recibir
// `{type:"SKIP_WAITING"}`; aquí, al detectar un SW nuevo INSTALADO habiendo ya un controller (=es
// una actualización, no la primera instalación), se lo pedimos → toma control → `controllerchange`
// → recargamos UNA vez. Así basta un refresh del usuario (antes el SW nuevo quedaba `waiting`).
// Dependency-free a propósito (evita workbox-window). Para volverlo TOTALMENTE automático (recargar
// pestañas inactivas sin acción del usuario) ver infra/PWA_UPDATES.md. En Tauri no hay SW → no-op.
/**
 * Telemetry beacon for PWA updates (infra/CLIENT_TELEMETRY_PROPAGATION.md).
 *
 * The GOOD outcome of a Service Worker update is a reload, which throws away
 * anything held in memory — so noting "applied" in module state and calling
 * `location.reload()` right after would mean the beacon reported `applied`
 * NEVER. The state is handed off through `sessionStorage` instead: written on
 * `controllerchange` (pre-reload) and read back on boot.
 *
 * Wrapped because `sessionStorage` throws outright in a partitioned/blocked
 * third-party context, and a telemetry beacon may not take the app down.
 */
const SW_UPDATE_HANDOFF_KEY = "fieldcheck.sw-update";

function rememberServiceWorkerUpdate(state: string): void {
  try {
    globalThis.sessionStorage?.setItem(SW_UPDATE_HANDOFF_KEY, state);
  } catch {
    // Storage denied — the beacon degrades to "none", the app is unaffected.
  }
}

if ("serviceWorker" in navigator) {
  // Read the hand-off left by the pre-reload page, if any.
  try {
    const carried = globalThis.sessionStorage?.getItem(SW_UPDATE_HANDOFF_KEY);
    if (carried === "applied") {
      setServiceWorkerUpdate("applied");
      globalThis.sessionStorage?.removeItem(SW_UPDATE_HANDOFF_KEY);
    }
  } catch {
    // Same rule: unreadable storage is not an error worth surfacing.
  }

  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) {
      return;
    }
    reloading = true;
    rememberServiceWorkerUpdate("applied");
    window.location.reload();
  });
  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // `register` puede resolver a `undefined` cuando el SW está bloqueado
        // (p. ej. e2e con `serviceWorkers: "block"`, o navegadores que lo desactivan).
        // Sin esta guarda, `reg.addEventListener` lanza un pageerror no atrapado.
        // El tipo TS marca `reg` como no nulo, pero en runtime sí puede serlo: la
        // regla type-aware es un falso positivo aquí (verificado: pageerror real).
        // oxlint-disable-next-line typescript/no-unnecessary-condition
        if (!reg) {
          return undefined;
        }
        reg.addEventListener("updatefound", () => {
          const next = reg.installing;
          if (!next) {
            return;
          }
          next.addEventListener("statechange", () => {
            // Solo en ACTUALIZACIÓN (ya hay controller); en la 1ª instalación no recargamos.
            if (next.state === "installed" && navigator.serviceWorker.controller) {
              // A new build is installed but not yet in control. If the device
              // keeps reporting "pending" it means the swap is STUCK — the exact
              // "we shipped a fix and nobody got it" case the census exists for.
              setServiceWorkerUpdate("pending");
              // `next` es un ServiceWorker, no una Window: su `postMessage(message, transfer?)`
              // NO acepta targetOrigin (ese 2º arg es la transfer-list). La regla, pensada para
              // Window.postMessage, es un falso positivo aquí.
              // oxlint-disable-next-line unicorn/require-post-message-target-origin
              next.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
        return undefined;
      })
      // Un registro bloqueado/denegado rechaza la promesa; tragarlo evita un
      // unhandledrejection que ensucia la consola (y rompe asserts de "sin
      // errores graves"). La PWA es una mejora progresiva: si no registra, la
      // app funciona igual.
      .catch(() => {
        // Beacon only: still swallowed, still no user-visible effect. A fleet
        // where this climbs means updates cannot reach anyone, which is
        // invisible from the server without this signal.
        setServiceWorkerUpdate("failed");
        return undefined;
      });
  });
}

const root = document.querySelector("#root");

if (!root) {
  throw new Error("Root element #root not found in index.html");
}
const rootEl: Element = root;

function mountApp(): void {
  render(() => <App />, rootEl);
}

if (isTauriRuntime()) {
  // Tauri DESKTOP: load the Bearer token(s) from the OS keychain into the
  // synchronous in-memory cache BEFORE the app mounts, so the very first
  // authenticated request resolves its token (keychain reads are async; the
  // hot read-path is sync). Best-effort — never block boot on a keychain error.
  try {
    await hydrateSecureTokens();
    // Phase 2: prime the RUST-held active token from the store so the
    // `api_request` proxy can inject the Bearer on the first authenticated
    // request. The token stays in Rust — JS never receives it.
    await primeActiveNativeToken();
  } catch (err) {
    console.warn("[secure-token] hydration failed, continuing:", err);
  }
}
// Web (cookie-BFF) renders immediately; Tauri renders once the cache is warm.
mountApp();

// Dismiss the first-paint loader baked into index.html once the app shell has
// mounted. We wait two animation frames so the freshly-rendered shell has
// actually painted before we fade the loader, avoiding a flash-to-blank.
//
// ⚠️ El doble `requestAnimationFrame` NO puede ser el ÚNICO camino. rAF no
// dispara en una pestaña de fondo, y cuando el compositor se queda sin CPU
// puede quedarse parado segundos o minutos. Si no dispara, esta pantalla de
// carga a pantalla completa (position:fixed, z-index máximo) se queda puesta
// PARA SIEMPRE encima de una app que ya está montada y funcionando: el usuario
// ve «Loading FieldCheck» sobre una aplicación viva y no puede tocar nada.
// Medido anulando `requestAnimationFrame`: `#root` ya tiene el shell renderizado
// y el loader sigue ahí. En la suite e2e esto se manifestaba como pantallazos
// «Loading FieldCheck» y timeouts opacos que cambiaban de test según la carga
// de la máquina. El `setTimeout` es la red de seguridad: si rAF cumplió, la
// guarda lo convierte en un no-op.
let appLoaderDismissed = false;
function dismissAppLoader() {
  if (appLoaderDismissed) {
    return;
  }
  appLoaderDismissed = true;
  const loader = document.querySelector("#app-loader");
  if (!loader) {
    return;
  }
  loader.classList.add("app-loader--hidden");
  const LOADER_FADE_MS = 300;
  setTimeout(() => {
    loader.remove();
  }, LOADER_FADE_MS);
}
requestAnimationFrame(() => {
  requestAnimationFrame(dismissAppLoader);
});
// Holgado respecto al camino normal (dos frames, ~32 ms), así que en un arranque
// sano nunca llega a decidir nada; solo actúa cuando rAF no va a venir.
const LOADER_FALLBACK_MS = 1500;
setTimeout(dismissAppLoader, LOADER_FALLBACK_MS);
// cache-warm probe 1781180306
// cache probe web-only 1781181006
// ci cache probe 2 1781182938
